import React, { useState, useEffect } from "react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftSpinner,
	CraftDivider,
} from "@craft-design-system/core-components-react";
import {
	Mail,
	ChevronDown as ExpandMore,
	Calendar,
	Users,
	FileText,
	ExternalLink,
	RefreshCw as Refresh,
} from "lucide-react";
import { apiClient } from "../../services/api";
import { getApiBaseUrl } from "../../utils/apiConfig";

const EmailHistoryModal = ({ open, onClose, fromEmail = "" }) => {
	const [emailHistory, setEmailHistory] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [expandedEmail, setExpandedEmail] = useState(null);

	// Fetch email history when modal opens
	useEffect(() => {
		if (open && fromEmail) {
			fetchEmailHistory();
		}
	}, [open, fromEmail]);
	const fetchEmailHistory = async () => {
		setLoading(true);
		setError("");

		try {
			const response = await apiClient.get(
				`/api/email-history?from_email=${encodeURIComponent(fromEmail)}`,
			);
			setEmailHistory(response.data.email_history || []);
		} catch (err) {
			console.error("Email history fetch error:", err);
			if (err.response?.data?.detail) {
				setError(err.response.data.detail);
			} else if (err.response?.status === 401) {
				setError("Authentication failed. Please log in again.");
			} else if (err.response?.status >= 500) {
				setError("Server error. Please try again later.");
			} else {
				setError(
					"Failed to fetch email history. Please check your internet connection and try again.",
				);
			}
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (isoString) => {
		if (!isoString) return "Unknown";

		try {
			const date = new Date(isoString);
			return date.toLocaleString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
		} catch (e) {
			return "Invalid Date";
		}
	};

	const handleEmailExpand = (emailId) => {
		setExpandedEmail(expandedEmail === emailId ? null : emailId);
	};

	const handleViewRecords = (viewUrl) => {
		if (viewUrl) {
			window.open(viewUrl, "_blank");
		}
	};
	const s = {
		meta: {
			display: "flex",
			alignItems: "center",
			gap: "6px",
			fontSize: "12px",
			color: "#666",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		label: {
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#555",
			marginBottom: "4px",
		},
		chip: {
			display: "inline-block",
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "12px",
			padding: "1px 10px",
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			margin: "2px",
		},
		chipCC: {
			display: "inline-block",
			backgroundColor: "#fff4e6",
			border: "1px solid #ffd699",
			borderRadius: "12px",
			padding: "1px 10px",
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			margin: "2px",
		},
		card: {
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			marginBottom: "12px",
			padding: "16px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			backgroundColor: "white",
		},
		subject: {
			fontSize: "15px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#1e2832",
			marginBottom: "8px",
		},
		recordsBox: {
			maxHeight: "180px",
			overflowY: "auto",
			border: "1px solid #e5e7eb",
			borderRadius: "6px",
		},
		recordRow: {
			padding: "8px 12px",
			borderBottom: "1px solid #e5e7eb",
			display: "flex",
			gap: "12px",
			flexWrap: "wrap",
			fontSize: "12px",
		},
		msgBox: {
			backgroundColor: "#f8f9fa",
			borderRadius: "6px",
			padding: "10px 14px",
			fontSize: "13px",
			whiteSpace: "pre-wrap",
			color: "#555",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		expandBtn: {
			background: "none",
			border: "none",
			cursor: "pointer",
			fontSize: "12px",
			color: "#666",
			display: "flex",
			alignItems: "center",
			gap: "4px",
			padding: "4px 8px",
			borderRadius: "4px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		viewBtn: {
			background: "none",
			border: "1px solid #cc0000",
			color: "#cc0000",
			cursor: "pointer",
			fontSize: "11px",
			borderRadius: "4px",
			padding: "3px 10px",
			display: "inline-flex",
			alignItems: "center",
			gap: "4px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
		},
		empty: {
			textAlign: "center",
			padding: "48px 20px",
			color: "#999",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		alert: {
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "12px",
			fontSize: "13px",
			backgroundColor: "#fde8e8",
			border: "1px solid #f5c6c6",
			color: "#cc0000",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		sectionTitle: {
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#374151",
			marginBottom: "6px",
		},
	};

	return (
		<CraftModal show={open} allowClickOut={false} onCraftClose={onClose}>
			<CraftModalHeader
				header="Email History — Sent Reminders"
				content={`Showing reminder emails sent from ${fromEmail || "your account"}.`}
			/>
			<CraftModalBody>
				{/* Refresh button rows */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "12px",
					}}
				>
					<span
						style={{
							fontSize: "13px",
							color: "#666",
							fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
						}}
					>
						{!loading &&
							`${emailHistory.length} email${emailHistory.length !== 1 ? "s" : ""} found`}
					</span>
					<button
						onClick={fetchEmailHistory}
						disabled={loading}
						style={{
							background: "none",
							border: "1px solid #ddd",
							borderRadius: "4px",
							padding: "5px 12px",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							gap: "5px",
							fontSize: "12px",
							fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
							color: "#555",
						}}
					>
						<Refresh size={13} /> Refresh
					</button>
				</div>

				{/* Loading */}
				{loading && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							padding: "40px",
							gap: "12px",
						}}
					>
						<CraftSpinner size="m" />
						<span
							style={{
								fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
								color: "#666",
							}}
						>
							Loading email history…
						</span>
					</div>
				)}

				{/* Error */}
				{error && <div style={s.alert}>⚠️ {error}</div>}

				{/* Email list */}
				{!loading && !error && (
					<div style={{ maxHeight: "540px", overflowY: "auto" }}>
						{emailHistory.length === 0 ? (
							<div style={s.empty}>
								<Mail
									size={48}
									style={{
										opacity: 0.2,
										display: "block",
										margin: "0 auto 12px",
									}}
								/>
								<div>No email history found</div>
								<div style={{ fontSize: "12px", marginTop: "4px" }}>
									Sent reminder emails will appear here
								</div>
							</div>
						) : (
							emailHistory.map((email, index) => {
								const emailId = email.id || index;
								const isExpanded = expandedEmail === emailId;
								return (
									<div key={emailId} style={s.card}>
										{/* Subject + actions row */}
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
												gap: "8px",
											}}
										>
											<div style={{ flex: 1 }}>
												<div style={s.subject}>{email.subject}</div>

												{/* Meta row */}
												<div
													style={{
														display: "flex",
														flexWrap: "wrap",
														gap: "14px",
														marginBottom: "10px",
													}}
												>
													<span style={s.meta}>
														<Calendar size={13} /> {formatDate(email.sent_at)}
													</span>
													<span style={s.meta}>
														<Users size={13} /> {email.to_emails?.length || 0}{" "}
														recipient(s)
														{email.cc_emails?.length > 0
															? ` + ${email.cc_emails.length} CC`
															: ""}
													</span>
													<span style={s.meta}>
														<FileText size={13} /> {email.record_count || 0}{" "}
														records
													</span>
												</div>

												{/* To chips */}
												<div style={{ marginBottom: "6px" }}>
													<div style={s.label}>To:</div>
													<div>
														{(email.to_emails || []).map((r, i) => (
															<span key={i} style={s.chip}>
																{r}
															</span>
														))}
													</div>
												</div>

												{/* CC chips */}
												{email.cc_emails?.length > 0 && (
													<div style={{ marginBottom: "6px" }}>
														<div style={s.label}>CC:</div>
														<div>
															{email.cc_emails.map((r, i) => (
																<span key={i} style={s.chipCC}>
																	{r}
																</span>
															))}
														</div>
													</div>
												)}
											</div>

											{/* Action buttons */}
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: "6px",
													flexShrink: 0,
												}}
											>
												{email.view_url && (
													<button
														style={s.viewBtn}
														onClick={() => handleViewRecords(email.view_url)}
													>
														<ExternalLink size={11} /> View Records
													</button>
												)}
												<button
													style={s.expandBtn}
													onClick={() => handleEmailExpand(emailId)}
												>
													{isExpanded ? "Less" : "More"}
													<ExpandMore
														size={12}
														style={{
															transform: isExpanded
																? "rotate(180deg)"
																: "rotate(0deg)",
															transition: "transform 0.2s",
														}}
													/>
												</button>
											</div>
										</div>

										{/* Expanded section */}
										{isExpanded && (
											<div
												style={{
													paddingTop: "12px",
													borderTop: "1px solid #e5e7eb",
													marginTop: "8px",
												}}
											>
												{/* Message */}
												<div style={{ marginBottom: "14px" }}>
													<div style={s.sectionTitle}>Message:</div>
													<div style={s.msgBox}>
														{email.message || "No message content"}
													</div>
												</div>

												{/* Records */}
												{email.selected_records?.length > 0 && (
													<div>
														<div style={s.sectionTitle}>
															Records ({email.selected_records.length}):
														</div>
														<div style={s.recordsBox}>
															{email.selected_records.map((record, ri) => (
																<div
																	key={ri}
																	style={{
																		...s.recordRow,
																		borderBottom:
																			ri < email.selected_records.length - 1
																				? "1px solid #e5e7eb"
																				: "none",
																	}}
																>
																	<span
																		style={{
																			fontFamily:
																				"MAN EUROPE_Bold, Arial, sans-serif",
																			minWidth: "60px",
																		}}
																	>
																		{record.vehicle_no || "N/A"}
																	</span>
																	<span style={{ minWidth: "100px" }}>
																		{record.part_no || "N/A"}
																	</span>
																	<span style={{ color: "#666", flex: 1 }}>
																		{record.part_description || "N/A"}
																	</span>
																</div>
															))}
														</div>
													</div>
												)}
											</div>
										)}
									</div>
								);
							})
						)}
					</div>
				)}
			</CraftModalBody>{" "}
			<CraftModalFooter>
				<CraftButton variant="primary" size="m" onClick={onClose}>
					Close
				</CraftButton>
			</CraftModalFooter>
		</CraftModal>
	);
};

export default EmailHistoryModal;
